import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * @typedef {object} IndexEntry
 * @property {string} label - the name as authored, used for display and matching
 * @property {string} key - lowercased name plus any aliases, used for matching
 * @property {string} categoryId - block id of the category card holding it
 * @property {string} categoryName - display name of that category
 * @property {boolean} isCategory - true when the entry IS a category, not a row
 */

/**
 * Contaminant explorer for the lab-results pages.
 *
 * Search model — this is the part that is easy to get wrong. Typing does NOT
 * filter or highlight anything. A partial query only produces a suggestion
 * list; a row is highlighted and pulled to the top of its table only once a
 * single contaminant has actually been chosen (from the suggestion list, a
 * top-search pill, or by typing a name in full). Highlighting every substring
 * match instead paints most of the page blue for a one-letter query.
 *
 * When nothing matches at all, the suggestion list is replaced by the
 * no-results message rather than leaving the query with no feedback.
 *
 * @extends {Component<{
 *   searchInput: HTMLInputElement,
 *   suggestions: HTMLElement,
 *   noResults: HTMLElement,
 *   noResultsTerm: HTMLElement,
 *   categoriesBtn: HTMLButtonElement,
 *   categoriesLabel: HTMLElement,
 *   categoriesList: HTMLElement,
 *   categoryOption: HTMLButtonElement[],
 *   pill: HTMLButtonElement[],
 * }>}
 */
class LabResultsContaminants extends Component {
  /** @type {AbortController | undefined} */
  #controller;

  #activeOption = -1;
  #activeSuggestion = -1;

  /**
   * Every searchable term on the page: one entry per category plus one per
   * contaminant. `key` carries the contaminant's aliases too, so "Chromium-6"
   * finds "Chromium (hexavalent)".
   * @type {IndexEntry[]}
   */
  #index = [];

  /** Document order of every mobile row, so a reorder can be undone. @type {Map<HTMLElement, HTMLElement[]>} */
  #mobileOrder = new Map();

  /** Label of the currently chosen contaminant, lowercased. '' when nothing is chosen. */
  #selected = '';

  /** Card currently marked `is-revealed`, so a later reveal can clear it. @type {HTMLElement | null} */
  #revealedCard = null;

  connectedCallback() {
    super.connectedCallback();

    this.#buildIndex();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener(
      'click',
      (event) => {
        const target = /** @type {HTMLElement} */ (event.target);

        const trigger = target.closest('[data-accordion-trigger]');
        if (trigger instanceof HTMLElement) {
          this.#toggleAccordion(trigger);
          return;
        }

        const suggestion = target.closest('[data-suggestion]');
        if (suggestion instanceof HTMLElement) {
          this.#choose(suggestion.dataset.suggestion ?? '');
          return;
        }

        const option = target.closest('[data-category-id]');
        if (option instanceof HTMLElement) {
          this.#selectCategory(option.dataset.categoryId ?? '');
          return;
        }

        const pill = target.closest('[data-term]');
        if (pill instanceof HTMLElement) {
          this.#choose(pill.dataset.term ?? '');
        }
      },
      { signal }
    );

    const onInput = debounce(() => this.#onQuery(this.refs.searchInput?.value ?? ''), 150);
    this.refs.searchInput?.addEventListener('input', onInput, { signal });
    this.refs.searchInput?.addEventListener('keydown', (event) => this.#onInputKeydown(event), { signal });

    this.refs.categoriesBtn?.addEventListener('click', () => this.#toggleDropdown(), { signal });
    this.refs.categoriesList?.addEventListener('keydown', (event) => this.#onListKeydown(event), { signal });
    this.refs.categoriesBtn?.addEventListener('keydown', (event) => this.#onButtonKeydown(event), { signal });

    document.addEventListener('pointerdown', this.#onDocumentPointerDown, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = undefined;
    this.#index = [];
    this.#mobileOrder.clear();
    this.#revealedCard = null;
  }

  #buildIndex() {
    /** @type {IndexEntry[]} */
    const index = [];

    for (const card of this.querySelectorAll('[data-accordion]')) {
      if (!(card instanceof HTMLElement)) continue;

      const categoryId = card.dataset.categoryId ?? '';
      const categoryName = card.dataset.categoryName ?? '';

      if (categoryName) {
        index.push({
          label: categoryName,
          key: categoryName.toLowerCase(),
          categoryId,
          categoryName,
          isCategory: true,
        });
      }

      // Desktop rows only: the mobile table repeats every contaminant, and a
      // duplicate entry would show the same suggestion twice.
      for (const row of card.querySelectorAll('.lr-table__row[data-name]')) {
        if (!(row instanceof HTMLElement)) continue;
        const label = row.dataset.label ?? '';
        if (!label) continue;
        index.push({
          label,
          key: row.dataset.name ?? label.toLowerCase(),
          categoryId,
          categoryName,
          isCategory: false,
        });
      }

      const tbody = card.querySelector('.lr-table-mobile__table tbody');
      if (tbody instanceof HTMLElement) {
        this.#mobileOrder.set(tbody, /** @type {HTMLElement[]} */ ([...tbody.children]));
      }
    }

    this.#index = index;
  }

  get #suggestionLimit() {
    return Number(this.dataset.suggestionLimit) || 8;
  }

  /**
   * @param {string} needle - lowercased, trimmed
   * @returns {IndexEntry[]}
   */
  #match(needle) {
    /** @type {Set<string>} */
    const seen = new Set();
    /** @type {IndexEntry[]} */
    const starts = [];
    /** @type {IndexEntry[]} */
    const contains = [];

    for (const entry of this.#index) {
      if (!entry.key.includes(needle)) continue;
      const dedupe = entry.label.toLowerCase();
      if (seen.has(dedupe)) continue;
      seen.add(dedupe);
      // A term the query starts is a better answer than one that merely
      // contains it, so "h" leads with Herbicides rather than Phthalates.
      (dedupe.startsWith(needle) ? starts : contains).push(entry);
    }

    return [...starts, ...contains].slice(0, this.#suggestionLimit);
  }

  /** @param {string} value - raw input value */
  #onQuery(value) {
    const needle = value.trim().toLowerCase();

    this.#selected = '';
    this.#clearHighlight();

    if (!needle) {
      this.#renderSuggestions([]);
      this.#setNoResults('');
      return;
    }

    const matches = this.#match(needle);

    // Typing a name out in full behaves like picking it from the list.
    if (matches[0] && matches[0].label.toLowerCase() === needle) {
      this.#renderSuggestions([]);
      this.#setNoResults('');
      this.#apply(matches[0]);
      return;
    }

    this.#renderSuggestions(matches);
    this.#setNoResults(matches.length ? '' : value.trim());
  }

  /** @param {string} term - a contaminant or category label */
  #choose(term) {
    const needle = term.trim().toLowerCase();
    if (!needle) return;

    const entry = this.#index.find((item) => item.label.toLowerCase() === needle) ?? this.#match(needle)[0];
    if (!entry) {
      if (this.refs.searchInput) this.refs.searchInput.value = term;
      this.#renderSuggestions([]);
      this.#setNoResults(term);
      return;
    }

    if (this.refs.searchInput) this.refs.searchInput.value = entry.label;
    this.#renderSuggestions([]);
    this.#setNoResults('');
    this.#apply(entry);
  }

  /** @param {IndexEntry} entry */
  #apply(entry) {
    this.#selected = entry.isCategory ? '' : entry.label.toLowerCase();
    this.#clearHighlight();

    if (!entry.isCategory) this.#highlight(entry);

    this.#openOnly(entry.categoryId);
    this.#setCategoryLabel(entry.categoryName);
    this.#revealCategory(entry.categoryId);
  }

  /** @param {IndexEntry} entry */
  #highlight(entry) {
    const card = this.#cardFor(entry.categoryId);
    if (!card) return;

    for (const row of card.querySelectorAll('[data-label]')) {
      if (!(row instanceof HTMLElement)) continue;
      if ((row.dataset.label ?? '').toLowerCase() !== this.#selected) continue;

      row.classList.add('is-match');

      // The desktop table reorders with flex `order`; a table row cannot, so
      // the mobile row is moved to the front of its own tbody instead.
      const tbody = row.parentElement;
      if (row.tagName === 'TR' && tbody instanceof HTMLElement) tbody.prepend(row);
    }
  }

  #clearHighlight() {
    for (const row of this.querySelectorAll('.is-match')) row.classList.remove('is-match');
    for (const [tbody, rows] of this.#mobileOrder) {
      for (const row of rows) tbody.appendChild(row);
    }
  }

  /** @param {IndexEntry[]} matches */
  #renderSuggestions(matches) {
    const list = this.refs.suggestions;
    if (!list) return;

    this.#activeSuggestion = -1;

    if (!matches.length) {
      list.replaceChildren();
      list.hidden = true;
      return;
    }

    list.replaceChildren(
      ...matches.map((entry) => {
        const li = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'lr-explorer__suggestion';
        button.dataset.suggestion = entry.label;
        button.textContent = entry.label;
        li.appendChild(button);
        return li;
      })
    );
    list.hidden = false;
  }

  /** @returns {HTMLButtonElement[]} */
  get #suggestionButtons() {
    return /** @type {HTMLButtonElement[]} */ ([...(this.refs.suggestions?.querySelectorAll('[data-suggestion]') ?? [])]);
  }

  /** @param {string} term - the query to echo, or '' to hide the message */
  #setNoResults(term) {
    const block = this.refs.noResults;
    if (!block) return;

    if (!term) {
      block.hidden = true;
      return;
    }

    if (this.refs.noResultsTerm) this.refs.noResultsTerm.textContent = term;
    block.hidden = false;
  }

  /** @param {string} id */
  #cardFor(id) {
    return this.querySelector(`#${CSS.escape(`lr-category-${id}`)}`);
  }

  /** @param {HTMLElement} trigger */
  #toggleAccordion(trigger) {
    const content = this.#contentFor(trigger);
    if (!content) return;

    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    content.hidden = expanded;
  }

  /**
   * @param {HTMLElement} trigger
   * @param {boolean} expanded
   */
  #setExpanded(trigger, expanded) {
    const content = this.#contentFor(trigger);
    trigger.setAttribute('aria-expanded', String(expanded));
    if (content) content.hidden = !expanded;
  }

  /** @param {HTMLElement} trigger */
  #contentFor(trigger) {
    const id = trigger.getAttribute('aria-controls');
    return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
  }

  /** @returns {HTMLElement[]} */
  get #triggers() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-accordion-trigger]')]);
  }

  /**
   * Choosing a result opens exactly one category — leaving the others open
   * buries the row that was just searched for under the categories above it.
   * @param {string} id
   */
  #openOnly(id) {
    const card = this.#cardFor(id);
    for (const trigger of this.#triggers) {
      this.#setExpanded(trigger, card ? card.contains(trigger) : false);
    }
  }

  /** @param {string} name */
  #setCategoryLabel(name) {
    const label = this.refs.categoriesLabel;
    if (!label) return;
    label.textContent = name || label.dataset.defaultLabel || label.textContent;
  }

  /** @param {string} id */
  #revealCategory(id) {
    const card = this.#cardFor(id);
    if (!card) return;

    this.#revealedCard?.classList.remove('is-revealed');
    card.classList.add('is-revealed');
    this.#revealedCard = /** @type {HTMLElement} */ (card);

    const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const top = card.getBoundingClientRect().top + window.scrollY - headerHeight - 32;

    window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: 'smooth' });
  }

  /** @param {string} id */
  #selectCategory(id) {
    this.#closeDropdown();

    if (this.refs.searchInput) this.refs.searchInput.value = '';
    this.#selected = '';
    this.#clearHighlight();
    this.#renderSuggestions([]);
    this.#setNoResults('');

    const card = this.#cardFor(id);
    if (!card) return;

    this.#openOnly(id);
    this.#setCategoryLabel(/** @type {HTMLElement} */ (card).dataset.categoryName ?? '');
    this.#revealCategory(id);
  }

  /** @returns {HTMLButtonElement[]} */
  get #options() {
    const options = this.refs.categoryOption;
    if (!options) return [];
    return Array.isArray(options) ? options : [options];
  }

  get #isOpen() {
    return this.refs.categoriesList?.hidden === false;
  }

  #toggleDropdown() {
    if (this.#isOpen) this.#closeDropdown();
    else this.#openDropdown();
  }

  #openDropdown() {
    const { categoriesList, categoriesBtn } = this.refs;
    if (!categoriesList || !categoriesBtn) return;

    this.#renderSuggestions([]);
    categoriesList.hidden = false;
    categoriesBtn.setAttribute('aria-expanded', 'true');
    this.#setActiveOption(0);
    categoriesList.focus();
  }

  #closeDropdown({ restoreFocus = false } = {}) {
    const { categoriesList, categoriesBtn } = this.refs;
    if (!categoriesList || !categoriesBtn) return;

    categoriesList.hidden = true;
    categoriesBtn.setAttribute('aria-expanded', 'false');
    categoriesList.removeAttribute('aria-activedescendant');

    const active = this.#options[this.#activeOption];
    active?.classList.remove('is-active');
    active?.setAttribute('aria-selected', 'false');
    this.#activeOption = -1;

    if (restoreFocus) categoriesBtn.focus();
  }

  /** @param {number} index */
  #setActiveOption(index) {
    const options = this.#options;
    if (!options.length) return;

    const next = (index + options.length) % options.length;
    const previous = options[this.#activeOption];
    previous?.classList.remove('is-active');
    previous?.setAttribute('aria-selected', 'false');

    const option = options[next];
    if (!option) return;

    option.classList.add('is-active');

    option.setAttribute('aria-selected', 'true');
    option.scrollIntoView({ block: 'nearest' });
    this.refs.categoriesList?.setAttribute('aria-activedescendant', option.id);
    this.#activeOption = next;
  }

  /** @param {number} index */
  #setActiveSuggestion(index) {
    const buttons = this.#suggestionButtons;
    if (!buttons.length) return;

    const next = (index + buttons.length) % buttons.length;
    buttons[this.#activeSuggestion]?.classList.remove('is-active');
    buttons[next]?.classList.add('is-active');
    buttons[next]?.scrollIntoView({ block: 'nearest' });
    this.#activeSuggestion = next;
  }

  /** @param {KeyboardEvent} event */
  #onInputKeydown(event) {
    const buttons = this.#suggestionButtons;

    switch (event.key) {
      case 'ArrowDown':
        if (!buttons.length) return;
        event.preventDefault();
        this.#setActiveSuggestion(this.#activeSuggestion + 1);
        break;
      case 'ArrowUp':
        if (!buttons.length) return;
        event.preventDefault();
        this.#setActiveSuggestion(this.#activeSuggestion - 1);
        break;
      case 'Enter': {
        event.preventDefault();
        const picked = buttons[this.#activeSuggestion] ?? buttons[0];
        if (picked) this.#choose(picked.dataset.suggestion ?? '');
        else this.#onQuery(this.refs.searchInput?.value ?? '');
        break;
      }
      case 'Escape':
        if (!buttons.length) return;
        event.preventDefault();
        this.#renderSuggestions([]);
        break;
    }
  }

  /** @param {KeyboardEvent} event */
  #onButtonKeydown(event) {
    if (this.#isOpen) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.#openDropdown();
      this.#setActiveOption(event.key === 'ArrowDown' ? 0 : this.#options.length - 1);
    }
  }

  /** @param {KeyboardEvent} event */
  #onListKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#setActiveOption(this.#activeOption + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#setActiveOption(this.#activeOption - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.#setActiveOption(0);
        break;
      case 'End':
        event.preventDefault();
        this.#setActiveOption(this.#options.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.#options[this.#activeOption]?.click();
        break;
      case 'Escape':
        event.preventDefault();
        this.#closeDropdown({ restoreFocus: true });
        break;
      case 'Tab':
        this.#closeDropdown();
        break;
    }
  }

  /** @param {Event} event */
  #onDocumentPointerDown = (event) => {
    const target = /** @type {Node} */ (event.target);

    if (
      this.refs.suggestions?.hidden === false &&
      !this.refs.suggestions.contains(target) &&
      !this.refs.searchInput?.contains(target)
    ) {
      this.#renderSuggestions([]);
    }

    if (!this.#isOpen) return;
    if (!this.refs.categoriesBtn?.contains(target) && !this.refs.categoriesList?.contains(target)) {
      this.#closeDropdown();
    }
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const next = /** @type {Node | null} */ (event.relatedTarget);
    if (next && this.contains(next)) return;

    this.#renderSuggestions([]);
    if (this.#isOpen) this.#closeDropdown();
  };
}

if (!customElements.get('lab-results-contaminants')) {
  customElements.define('lab-results-contaminants', LabResultsContaminants);
}
